<template>
    <!-- Parent Row -->
    <SmartLinkItemComponent custom-tag="tr" :id="model.system.id" @click="downloadFile(data.fileUrl)"
        :component="!model.system.workflow">
        <td class="td-title">
            <SmartLinkStandaloneElement :codename="typ.elements.title.codename">
                <div class="document-title">
                    <font-awesome class="icon-type" :icon="getIcon(data.type)" />
                    <div class="text" v-if="data.type?.codename == 'external_link'">
                        <a target="_blank" :href="data.externalLink">{{ data.title }}</a>
                    </div>
                    <div class="text" v-else>{{ data.title }}</div>
                </div>
            </SmartLinkStandaloneElement>
        </td>
        <td class="td-date" v-if="hasAnyDate">
            <SmartLinkStandaloneElement :codename="typ.elements.date.codename">
                {{ toSiteDateString(data.date) }}
            </SmartLinkStandaloneElement>
        </td>
        <td class="td-icon">
            <SmartLinkStandaloneElement :codename="typ.elements.file.codename">
                <MyLink class="icon-download" aria-label="Download Document" :to="data.fileUrl">
                    <font-awesome class="icon-download" icon="download" />
                    <span class="label-mobile">{{ $t('download') }}</span>
                </MyLink>
            </SmartLinkStandaloneElement>
        </td>
    </SmartLinkItemComponent>

    <!-- Child Row -->
    <template v-for="child in data.childItems">        
        <SmartLinkItemComponent custom-tag="tr" :id="model.system.id" 
            :component="!model.system.workflow" @click="downloadFile(firstOrDefault(child.elements.file.value)?.url)">
            <SmartLinkStandaloneElement custom-tag="td" colspan="3" class="p-0"
                :codename="typ.elements.child_items.codename">
                <table class="document-child-table w-100 p-0">
                    <tbody>
                        <tr>
                            <td class="td-title">
                                <div class="document-title ps-5">
                                    <font-awesome class="icon-type" icon="file-pdf" />
                                    <div class="text">{{ child.elements.title.value }}</div>
                                </div>
                            </td>
                            <td v-if="hasAnyDate" class="td-date">
                                <span>
                                    {{ toSiteDateString(child.elements.date.value ? new Date(child.elements.date.value) : undefined) }}
                                </span>
                            </td>
                            <td class="td-icon">
                                <span>
                                    <MyLink class="icon-download" aria-label="Download Document"
                                        :to="firstOrDefault(child.elements.file.value)?.url ?? ''"
                                        @click.prevent.stop="downloadFile(firstOrDefault(child.elements.file.value)?.url ?? '')">
                                        <font-awesome class="icon-download" icon="download" />
                                        <span class="label-mobile">{{ $t('download') }}</span>
                                    </MyLink>
                                </span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </SmartLinkStandaloneElement>
        </SmartLinkItemComponent>   
        
        <!-- Grand child Row -->
        <SmartLinkItemComponent custom-tag="tr" :id="model.system.id" :component="!model.system.workflow"
            v-for="grandChild in child.grandchildItems" @click="downloadFile(firstOrDefault(grandChild.elements.file.value)?.url)">
            <SmartLinkStandaloneElement custom-tag="td" colspan="3" class="p-0"
                :codename="typ.elements.child_items.codename">
                <table class="document-child-table grand-child-table w-100 p-0">
                    <tbody>
                        <tr>
                            <td class="td-title">
                                <div class="document-title ps-5">
                                    <font-awesome class="icon-type" icon="file-pdf" />
                                    <div class="text">{{ grandChild.elements.title.value }}</div>
                                </div>
                            </td>
                            <td v-if="hasAnyDate" class="td-date">
                                <span>
                                    {{ toSiteDateString(grandChild.elements.date.value ? new Date(grandChild.elements.date.value) : undefined) }}
                                </span>
                            </td>
                            <td class="td-icon">
                                <span>
                                    <MyLink class="icon-download" aria-label="Download Document"
                                        :to="firstOrDefault(grandChild.elements.file.value)?.url ?? ''"
                                        @click.prevent.stop="downloadFile(firstOrDefault(grandChild.elements.file.value)?.url ?? '')">
                                        <font-awesome class="icon-download" icon="download" />
                                        <span class="label-mobile">{{ $t('download') }}</span>
                                    </MyLink>
                                </span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </SmartLinkStandaloneElement>
        </SmartLinkItemComponent>
    </template>

</template>

<script lang="ts" setup>
import type { DocumentModel } from '~/models';
import { contentTypes } from '~/models';
import { firstOrDefault } from '~/utils/first-or-default';
import { toSiteDateString } from '~/utils/kontent-helper';
import { getIcon } from '~/utils/icons-util';

const typ = contentTypes.document;

const props = defineProps<{
    model: DocumentModel;
    hasAnyDate: boolean;
}>();

const pageStore = usePageStore();
const { downloadFile } = usePdfTracker()

const data = computed(() => {
    const childItems = pageStore.getListAs<DocumentModel>(props.model.elements.childItems.value)
        .map(child => ({
            ...child,
            grandchildItems: pageStore.getListAs<DocumentModel>(child.elements.childItems?.value || [])
        }));

    return {
        title: props.model.elements.title.value,
        externalLink: props.model.elements.externalLink.value,
        date: props.model.elements.date.value ? new Date(props.model.elements.date.value) : undefined,
        fileUrl: firstOrDefault(props.model.elements.file.value)?.url || '',
        type: firstOrDefault(props.model.elements.type.value),
        childItems
    };
});
</script>